// Anke, 2026-07-16 — Export als echte Vektorgrafik (SVG).
//
// WARUM NEU GEZEICHNET WIRD: Konva malt auf eine Canvas, also Pixel. Aus der
// Zeichenflaeche laesst sich kein Vektor herausziehen (`toDataURL` liefert nur
// ein Bild). Der Export baut die Grafik deshalb aus den OBJEKTDATEN neu auf.
//
// Wo immer moeglich werden dieselben Helfer benutzt wie beim Zeichnen
// (computeLineProps/splineControlPoints fuer Kurven, tintKreis/tintFill ueber
// avatarSvgString/smileySvgString fuer die Motive, konvaDashPattern fuer
// Strichmuster, computeStickyFontSize fuer die Haftnotiz-Schrift). So kann die
// Ausgabe nicht von der Anzeige abdriften.
//
// WAS NICHT VEKTOR WIRD — ehrlich benannt:
//  - Eingefuegte Bilder sind Pixel und bleiben Pixel (als Data-URL eingebettet).
//  - Textboxen werden als HTML in einem `<foreignObject>` eingebettet. Das ist
//    VEKTOR und exakt wie am Bildschirm, aber nur BROWSER stellen es dar —
//    Illustrator/Inkscape zeigen dort nichts. Der Weg ist bewusst gewaehlt: Er
//    erhaelt alle Formatierungen und ist zugleich die Grundlage fuer ein spaeter
//    ueber den Browser-Druck erzeugtes Vektor-PDF.

import type { BoardObject } from '../interfaces/board-object.interface.js';
import type { LineObject } from '../../features/line/line.interface.js';
import type { StickyNote } from '../../features/sticky/sticky.interface.js';
import type { TextBox } from '../../features/textbox/textbox.interface.js';
import type { PersonObject } from '../../features/person/person.interface.js';
import type { SmileyObject } from '../../features/smiley/smiley.interface.js';
import type { LinePathStrategyRegistry } from '../../features/line/strategies/line-path-strategy.interface.js';
import { computeLineProps, splineControlPoints } from '../../features/line/line.renderer.js';
import { konvaDashPattern } from '../../objects/konva-decoration.helper.js';
import {
  computeStickyFontSize, STICKY_FONT_FAMILY,
} from '../../features/sticky/sticky.renderer.js';
import { BOARD_TEXT_LINE_HEIGHT } from '../../constants/whiteboard-config.constants.js';
import {
  avatarSvgString, iconBox as personIconBox,
  PERSON_ICON_TEXT_GAP, PERSON_ICON_RATIO, PERSON_FONT_SIZE,
} from '../../features/person/person.renderer.js';
import { smileySvgString } from '../../features/smiley/smiley.renderer.js';
import { buildTextboxTextSvg, type Measure } from './textbox-to-svg-text.js';
import { alignRectOf } from '../command/align-objects.helper.js';
import {
  num, esc, attr, polylinePath, splinePath, unionBox, toPortableFonts,
  PORTABLE_FONT_FAMILY, type Box,
} from './svg-geometry.js';

/** Luft rundherum, damit Striche am Rand nicht abgeschnitten wirken. */
const MARGIN = 16;

export interface SvgExportOptions {
  /** Nur diese Objekte exportieren (Auswahl-Export). Sonst: alles. */
  readonly only?: ReadonlySet<string>;
  /** Linien-Strategien — noetig, um Kurven exakt wie am Bildschirm zu rechnen. */
  readonly lineRegistry: LinePathStrategyRegistry;
}

// ---- Hilfen ---------------------------------------------------------------

/** Umschlag mit Drehung: Konva dreht um den Objekt-Ursprung (obj.x/obj.y). */
function openGroup(obj: BoardObject): string {
  const rot = obj.rotation ? ` rotate(${num(obj.rotation)})` : '';
  return `<g transform="translate(${num(obj.x)} ${num(obj.y)})${rot}">`;
}

function dashAttr(style: unknown, strokeWidth: number): string {
  const dash = konvaDashPattern(style as never, strokeWidth);
  return dash.length ? ` stroke-dasharray="${dash.map(num).join(' ')}"` : '';
}

/**
 * Bricht Text auf eine Breite um — dieselbe gierige Wortumbruch-Regel, die
 * Konva benutzt. Gemessen wird mit einer Mess-Canvas; ohne Canvas (Testumgebung)
 * wird grob nach Zeichenbreite geschaetzt, damit der Export nie ganz ausfaellt.
 */
function wrapText(text: string, maxWidth: number, fontSize: number, fontFamily: string): string[] {
  const measure = createMeasurer(fontSize, fontFamily);
  const lines: string[] = [];
  for (const paragraph of text.split('\n')) {
    let line = '';
    for (const word of paragraph.split(/\s+/)) {
      if (!word) continue;
      const probe = line ? `${line} ${word}` : word;
      if (line && measure(probe) > maxWidth) {
        lines.push(line);
        line = word;
      } else {
        line = probe;
      }
    }
    lines.push(line);
  }
  return lines;
}

function createMeasurer(fontSize: number, fontFamily: string): (s: string) => number {
  try {
    const canvas = document.createElement('canvas');
    const ctx = canvas.getContext('2d');
    if (ctx) {
      ctx.font = `${fontSize}px ${fontFamily}`;
      return (s) => ctx.measureText(s).width;
    }
  } catch {
    // Kein DOM/Canvas — unten die Schaetzung.
  }
  return (s) => s.length * fontSize * 0.5;
}

/**
 * Breite eines Textstuecks in seiner Auszeichnung — gemessen mit einer
 * Mess-Canvas, damit der Umbruch dem entspricht, was man am Bildschirm sieht.
 * Ohne Canvas (Testumgebung) grob nach Zeichenbreite geschaetzt.
 */
const measureRun: Measure = (text, run) => {
  const weight = run.bold ? 'bold ' : '';
  const style = run.italic ? 'italic ' : '';
  // `sans-serif` bleibt die Messschrift wie bisher — ein Lauf trägt seit
  // @o2d/rich-text-editor 0.3.0 keine eigene Schriftart mehr. Dass gemessen mit
  // `sans-serif` und gezeichnet mit der portablen Export-Schrift wird, ist ein
  // vorbestehender Unterschied; ihn anzugleichen würde jede Umbruchstelle in
  // jedem Export verschieben und gehört nicht in diese Änderung.
  const font = `${style}${weight}${run.fontSize}px sans-serif`;
  const ctx = sharedMeasureContext();
  if (ctx) {
    ctx.font = font;
    return ctx.measureText(text).width;
  }
  return text.length * run.fontSize * 0.5;
};

let measureCtx: CanvasRenderingContext2D | null | undefined;
function sharedMeasureContext(): CanvasRenderingContext2D | null {
  if (measureCtx !== undefined) return measureCtx;
  try {
    measureCtx = document.createElement('canvas').getContext('2d');
  } catch {
    measureCtx = null;
  }
  return measureCtx;
}

/** Mehrzeiliger Text als `<text>` mit einer `<tspan>`-Zeile je Umbruch. */
function textBlock(
  lines: readonly string[],
  x: number, yFirst: number, lineHeight: number,
  attrs: string,
): string {
  if (lines.length === 0) return '';
  const spans = lines
    .map((l, i) => `<tspan x="${num(x)}" y="${num(yFirst + i * lineHeight)}">${esc(l)}</tspan>`)
    .join('');
  return `<text${attrs}>${spans}</text>`;
}

/** Nimmt vom fremden SVG nur den Inhalt und die viewBox — zum Verschachteln. */
function svgInner(svg: string): { inner: string; viewBox: string } {
  const vb = /viewBox="([^"]+)"/.exec(svg)?.[1] ?? '';
  const inner = svg.replace(/^[\s\S]*?<svg[^>]*>/, '').replace(/<\/svg>\s*$/, '');
  return { inner, viewBox: vb };
}

// ---- Objekt-Uebersetzer ---------------------------------------------------

function rectangleSvg(o: BoardObject & Record<string, never>): string {
  const r = o as unknown as {
    width: number; height: number; fill: string; strokeColor: string;
    strokeWidth: number; cornerRadius: number; strokeStyle?: unknown; backgroundOpacity?: number;
  };
  return `<rect x="0" y="0" width="${num(r.width)}" height="${num(r.height)}"` +
    attr('rx', r.cornerRadius || undefined) +
    ` fill="${r.fill || 'none'}" stroke="${esc(r.strokeColor)}" stroke-width="${num(r.strokeWidth)}"` +
    dashAttr(r.strokeStyle, r.strokeWidth) +
    attr('opacity', r.backgroundOpacity ?? undefined) + '/>';
}

function ellipseSvg(o: BoardObject): string {
  const e = o as unknown as {
    width: number; height: number; fill: string; strokeColor: string;
    strokeWidth: number; strokeStyle?: unknown; backgroundOpacity?: number;
  };
  return `<ellipse cx="${num(e.width / 2)}" cy="${num(e.height / 2)}"` +
    ` rx="${num(e.width / 2)}" ry="${num(e.height / 2)}"` +
    ` fill="${e.fill || 'none'}" stroke="${esc(e.strokeColor)}" stroke-width="${num(e.strokeWidth)}"` +
    dashAttr(e.strokeStyle, e.strokeWidth) +
    attr('opacity', e.backgroundOpacity ?? undefined) + '/>';
}

function checkboxSvg(o: BoardObject): string {
  const c = o as unknown as { width: number; height: number; checked?: boolean };
  const w = c.width;
  const strokeW = Math.max(2, w * 0.06);
  const box = `<rect x="0" y="0" width="${num(w)}" height="${num(c.height)}" rx="4"` +
    ` fill="#ffffff" stroke="#374151" stroke-width="${num(strokeW)}"/>`;
  if (!c.checked) return box;
  // Haken als Polylinie, wie im Renderer.
  const p = polylinePath([w * 0.22, w * 0.52, w * 0.42, w * 0.72, w * 0.78, w * 0.28]);
  return box + `<path d="${p}" fill="none" stroke="#374151" stroke-width="${num(strokeW)}"` +
    ' stroke-linecap="round" stroke-linejoin="round"/>';
}

function stickySvg(o: StickyNote): string {
  const fold = Math.min(o.width, o.height) * 0.18;
  const bg = `<rect x="0" y="0" width="${num(o.width)}" height="${num(o.height)}" fill="${esc(o.color)}"/>`;
  // Umgeknickte Ecke unten rechts (im Renderer eine geschlossene Linie).
  const tri = `<path d="M ${num(o.width - fold)} ${num(o.height)} L ${num(o.width)} ${num(o.height - fold)}` +
    ` L ${num(o.width)} ${num(o.height)} Z" fill="rgba(0,0,0,0.12)"/>`;
  if (!o.text) return bg + tri;

  const fontSize = computeStickyFontSize(o.width, o.height, o.text);
  const pad = o.width * 0.08;
  const lineHeight = fontSize * BOARD_TEXT_LINE_HEIGHT;
  const lines = wrapText(o.text, o.width - 2 * pad, fontSize, STICKY_FONT_FAMILY);
  const text = textBlock(lines, pad, pad + fontSize, lineHeight,
    ` font-family="${esc(PORTABLE_FONT_FAMILY)}" font-size="${num(fontSize)}" fill="#1f2937"`);
  return bg + tri + text;
}

function lineSvg(o: LineObject, registry: LinePathStrategyRegistry): string {
  const props = computeLineProps(o, registry);
  const pts = props.points as number[];
  const tension = props.tension ?? 0;
  // Kurven exakt wie am Bildschirm: Konva zeichnet einen Kardinal-Spline,
  // splinePath rechnet ihn mit DERSELBEN Formel in Bezier um.
  const d = tension > 0 ? splinePath(pts, tension, splineControlPoints) : polylinePath(pts);
  if (!d) return '';
  return `<path d="${d}" fill="none" stroke="${esc(o.strokeColor)}"` +
    ` stroke-width="${num(o.strokeWidth)}" stroke-linecap="round" stroke-linejoin="round"` +
    dashAttr(o.strokeStyle, o.strokeWidth) + '/>';
}

function personSvg(o: PersonObject): string {
  const box = personIconBox(o);
  const { inner, viewBox } = svgInner(avatarSvgString(o));
  // Inhalt mittig: dieselbe Rechnung wie applyBlockLayout im Renderer.
  const iconH = o.height * PERSON_ICON_RATIO;
  const fontSize = o.fontSize ?? PERSON_FONT_SIZE;
  const contentH = iconH + PERSON_ICON_TEXT_GAP + (o.text ? fontSize * BOARD_TEXT_LINE_HEIGHT : 0);
  const top = Math.max(0, (o.height - contentH) / 2);

  const avatar = `<svg x="${num(box.x)}" y="${num(top)}" width="${num(box.width)}"` +
    ` height="${num(box.height)}"${attr('viewBox', viewBox)}>${inner}</svg>`;
  if (!o.text) return avatar;

  const lines = wrapText(o.text, box.width, fontSize, STICKY_FONT_FAMILY);
  const label = textBlock(lines, box.x + box.width / 2, top + iconH + PERSON_ICON_TEXT_GAP + fontSize,
    fontSize * BOARD_TEXT_LINE_HEIGHT,
    ` font-family="${esc(PORTABLE_FONT_FAMILY)}" font-size="${num(fontSize)}"` +
    ` fill="${esc(o.textColor ?? o.color ?? '#374151')}" text-anchor="middle"`);
  return avatar + label;
}

function smileySvg(o: SmileyObject): string {
  const { inner, viewBox } = svgInner(smileySvgString(o));
  return `<svg x="0" y="0" width="${num(o.width)}" height="${num(o.height)}"` +
    `${attr('viewBox', viewBox)}>${inner}</svg>`;
}

function textboxSvg(o: TextBox): string {
  const bg = o.background
    ? `<rect x="0" y="0" width="${num(o.width)}" height="${num(o.height)}"` +
      attr('rx', o.cornerRadius) + ` fill="${esc(o.background)}"/>`
    : '';
  const border = o.borderColor && o.borderWidth > 0
    ? `<rect x="0" y="0" width="${num(o.width)}" height="${num(o.height)}"` +
      attr('rx', o.cornerRadius) +
      ` fill="none" stroke="${esc(o.borderColor)}" stroke-width="${num(o.borderWidth)}"/>`
    : '';
  // ECHTER SVG-Text statt eingebettetem HTML (Anke, 2026-07-16): nur so ist der
  // Text auch ausserhalb eines Browsers sichtbar und bearbeitbar.
  const content = buildTextboxTextSvg(o, measureRun);
  return bg + content + border;
}

function imageSvg(o: BoardObject): string {
  const i = o as unknown as { width: number; height: number; dataUrl: string };
  if (!i.dataUrl) return '';
  return `<image x="0" y="0" width="${num(i.width)}" height="${num(i.height)}"` +
    ` href="${esc(i.dataUrl)}" preserveAspectRatio="none"/>`;
}

/** Ein Objekt als SVG-Fragment (ohne den umgebenden `<g>`). */
function objectBody(obj: BoardObject, registry: LinePathStrategyRegistry): string {
  switch (obj.type) {
    case 'rectangle': return rectangleSvg(obj as never);
    case 'ellipse':   return ellipseSvg(obj);
    case 'checkbox':  return checkboxSvg(obj);
    case 'sticky':    return stickySvg(obj as StickyNote);
    case 'line':      return lineSvg(obj as LineObject, registry);
    case 'person':    return personSvg(obj as PersonObject);
    case 'smiley':    return smileySvg(obj as SmileyObject);
    case 'text':      return textboxSvg(obj as TextBox);
    case 'image':     return imageSvg(obj);
    default:          return '';
  }
}

// ---- Oeffentlich ----------------------------------------------------------

/**
 * Baut die ganze Zeichenflaeche (oder nur die Auswahl) als SVG-Zeichenkette.
 * Gibt `null` zurueck, wenn nichts zu exportieren ist — der Aufrufer soll dann
 * keine leere Datei erzeugen.
 */
export function buildBoardSvg(
  objects: readonly BoardObject[],
  opts: SvgExportOptions,
): string | null {
  const chosen = opts.only ? objects.filter((o) => opts.only!.has(o.id)) : objects;
  if (chosen.length === 0) return null;

  // Zuschnitt: die gedrehten Huellen aller Objekte (alignRectOf beruecksichtigt
  // die Drehung — dieselbe Funktion, die auch das Ausrichten benutzt).
  const boxes: Box[] = chosen.map((o) => alignRectOf(o));
  const bounds = unionBox(boxes)!;
  const x = bounds.x - MARGIN;
  const y = bounds.y - MARGIN;
  const w = Math.max(1, bounds.width + 2 * MARGIN);
  const h = Math.max(1, bounds.height + 2 * MARGIN);

  // Reihenfolge = Zeichenreihenfolge des Boards (hinten zuerst).
  const body = chosen
    .map((o) => {
      const inner = objectBody(o, opts.lineRegistry);
      return inner ? `${openGroup(o)}${inner}</g>` : '';
    })
    .filter(Boolean)
    .join('\n  ');

  // Schriftangaben portabel machen — betrifft auch die Schriften tief im
  // eingebetteten Textbox-HTML, deshalb ueber die FERTIGE Datei.
  return toPortableFonts(`<?xml version="1.0" encoding="UTF-8"?>
<svg xmlns="http://www.w3.org/2000/svg" xmlns:xlink="http://www.w3.org/1999/xlink"` +
    ` width="${num(w)}" height="${num(h)}" viewBox="${num(x)} ${num(y)} ${num(w)} ${num(h)}">
  ${body}
</svg>
`);
}
